import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import {
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetFilters } from '../../assets/components/assetFilters';
import { Issue } from '../../issues/components/issue';
import { useWindowDimensions } from '../../shared/hooks/useWindowDimensions';
import { Activities as ActivitiesOld } from './activities-old';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

export const Activities = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <ActivitiesBase />;
  }

  return <ActivitiesOld />;
};

const ActivitiesBase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { issueId, assetId } = useParams();
  const skipAutoSelect = (location.state as any)?.skipAutoSelect;
  const windowDimensions = useWindowDimensions();
  const windowHeight = windowDimensions?.height ?? 0;

  const maxHeight = windowHeight - 100;

  const onIssueSelected = useCallback(
    (issueId: string) => {
      navigate(`/activities/${issueId}`);
    },
    [navigate]
  );

  const handleDataLoaded = useCallback(
    (data: any[]) => {
      if (data?.length > 0 && !issueId && !skipAutoSelect) {
        onIssueSelected(data[0].id);
      }
    },
    [issueId, onIssueSelected, skipAutoSelect]
  );

  const activityListColumn = {
    title: 'activity.menu.title',
    mainComponent: (
      <ActivityLeftColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        selectedIssue={issueId}
        maxHeight={maxHeight}
        onDataLoaded={handleDataLoaded}
        hideFilterBar={true}
      />
    ),
    headerComponent: <ActivityFilters />,
    disableScroll: true,
  };

  // When assetId is present (route: /activities/:issueId/asset/:assetId), we switch to a
  // split view: Issue on the left, AssetCard on the right. This keeps both panels visible
  // without leaving the Activities context.
  const leftColumn = assetId
    ? {
        showHeader: false,
        disableScroll: true,
        mainComponent: <Issue isComingFromActivities />,
      }
    : activityListColumn;

  const rightColumn = assetId
    ? {
        title: 'assets.menu.titleSingular',
        showHeader: true,
        showBackButton: true,
        onBackButtonPress: () => navigate(-1),
        headerComponent: <AssetFilters assetId={assetId} />,
        disableScroll: false,
        mainComponent: <AssetCard assetId={assetId} pageSize={3} />,
      }
    : {
        title: 'activities.detail.title',
        showHeader: false,
        disableScroll: true,
        mainComponent: <Issue isComingFromActivities />,
      };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={issueId ? rightColumn : undefined}
      viewMode={issueId ? ViewMode.DUAL : ViewMode.SINGLE}
      transitionKey={assetId}
    />
  );
};

const ActivityLeftColumn = ({
  onIssueSelected,
  showAssetName,
  selectedIssue,
  maxHeight,
  onDataLoaded,
  hideFilterBar,
}: {
  onIssueSelected: (issueId: string) => void;
  showAssetName: boolean;
  selectedIssue?: string;
  maxHeight: number;
  onDataLoaded: (data: unknown[]) => void;
  hideFilterBar: boolean;
}) => {
  const { onSlideChange, currentViewMode } = useSlideLayout();

  const handleIssueSelected = useCallback(
    (issueId: string) => {
      if (currentViewMode !== ViewMode.DUAL) {
        onSlideChange(SlideAction.DUAL);
      }
      onIssueSelected(issueId);
    },
    [onSlideChange, currentViewMode, onIssueSelected]
  );

  return (
    <ActivityFoldOneColumn
      onIssueSelected={handleIssueSelected}
      showAssetName={showAssetName}
      selectedIssue={selectedIssue}
      maxHeight={maxHeight}
      onDataLoaded={onDataLoaded}
      hideFilterBar={hideFilterBar}
    />
  );
};
